import { secondsToTimecode } from '../../helpers/timecodeUtils';
import { historyStore, cleanItem } from '../historyStore';
import { queryClient, queryKeys } from '../../lib/query-client';
import ProjectService from '../../service/project-service';
import displayNetworkError from '../../helpers/displayNetworkError';
import handleErrorAndDisplay from '../../helpers/handleErrorAndDisplay';

export const makeCuesheetActions = (set, get) => {
    const fetchCueSheetImpl = async (id) => {
        set('cueSheetLoading', true);
        set('cueSheetError', null);

        try {
            const { items, history: historyData } = await queryClient.fetchQuery({
                queryKey: queryKeys.cueSheet(id),
                queryFn: async () => {
                    const [items, history] = await Promise.all([
                        ProjectService.getProjectCueSheetAsync(id),
                        ProjectService.getProjectHistoryAsync(id).catch(() => []),
                    ]);
                    return { items, history };
                },
            });

            const normalizedData = items.map(item => ({
                ...item,
                instanceDuration: item.instanceDuration ?? item.clip?.duration ?? 0,
                priority: item.priority ?? 0,
                startPlayback: item.startPlayback ?? 0,
            }));

            set('cueSheet', normalizedData);
            set('cueSheetLoading', false);

            const history = historyStore.getState();
            if (historyData && (Array.isArray(historyData) ? historyData.length > 0 : historyData?.body)) {
                history.load(historyData);
            } else {
                history.clear();
            }
        } catch (error) {
            set('cueSheetError', error);
            set('cueSheetLoading', false);
            displayNetworkError(error);

            if (!error?.handled) {
                throw error;
            }
        }
    };

    const refetchCueSheetImpl = async (id) => {
        await queryClient.invalidateQueries({ queryKey: queryKeys.cueSheet(id) });
        await fetchCueSheetImpl(id);
    };

    return {
        fetchCueSheetImpl,
        refetchCueSheetImpl,

        fetchCueSheet: fetchCueSheetImpl,
        refetchCueSheet: refetchCueSheetImpl,

        createCueSheetItem: async (id, formData) => {
            try {
                await ProjectService.createProjectCueSheetItemAsync(id, formData);

                await queryClient.invalidateQueries({ queryKey: queryKeys.cueSheet(id) });
                await refetchCueSheetImpl(id);
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        importCueSheetItems: async (id, formData) => {
            try {
                await ProjectService.importProjectCueSheetItemsAsync(id, formData);

                await queryClient.invalidateQueries({ queryKey: queryKeys.cueSheet(id) });
                await refetchCueSheetImpl(id);
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        deleteCueSheetItem: async (id, itemId) => {
            try {
                await ProjectService.deleteProjectCueSheetItemAsync(id, itemId);

                await queryClient.invalidateQueries({ queryKey: queryKeys.cueSheet(id) });
                await fetchCueSheetImpl(id);
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        saveCueSheetUpdates: async (projectId, updates) => {
            try {
                const currentCueSheet = get('cueSheet');
                const updatedCueSheet = currentCueSheet.map(item => {
                    const update = updates.find(u => u.id === item.id);
                    if (update) {
                        return {
                            ...item,
                            ...(update.title !== undefined && { title: update.title }),
                            ...(update.label !== undefined && { label: update.label }),
                            ...(update.timecode !== undefined && { timecode: update.timecode }),
                            ...(update.instanceDuration !== undefined && { instanceDuration: update.instanceDuration }),
                            ...(update.priority !== undefined && { priority: update.priority }),
                            ...(update.startPlayback !== undefined && { startPlayback: update.startPlayback }),
                            ...(update.status !== undefined && { status: update.status }),
                        };
                    }
                    return item;
                });
                set('cueSheet', updatedCueSheet);
                historyStore.getState().onCueSheetUpdated(projectId);
            } catch (error) {
                handleErrorAndDisplay(error);
                const refetchCueSheet = get('refetchCueSheet');
                if (refetchCueSheet) {
                    await refetchCueSheetImpl(projectId);
                }

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        cutCueSheetItem: async (projectId, originalItem, firstCutDuration, secondCutStart, secondCutDuration, secondCutStartPlayback, newId) => {
            const frameRate = 24;

            historyStore.getState().beginAction('Cut');

            const base = cleanItem(originalItem);

            const firstCut = {
                ...base,
                instanceDuration: Math.round(firstCutDuration * 1000),
            };

            const secondCut = {
                ...base,
                id: newId,
                timecode: secondsToTimecode(secondCutStart, frameRate),
                instanceDuration: Math.round(secondCutDuration * 1000),
                startPlayback: secondCutStartPlayback,
            };

            const currentCueSheet = get('cueSheet');
            const updatedCueSheet = [
                ...currentCueSheet.map(item => item.id === originalItem.id ? firstCut : item),
                secondCut,
            ];

            set('cueSheet', updatedCueSheet);
            historyStore.getState().onCueSheetUpdated(projectId);

            try {
                await ProjectService.cutProjectCueSheetItemAsync(
                    projectId,
                    originalItem.id,
                    firstCut,
                    secondCut,
                );
            } catch (error) {
                handleErrorAndDisplay(error);
                await queryClient.invalidateQueries({ queryKey: queryKeys.cueSheet(projectId) });
            }
        },

        saveHistoryItems: async (projectId, items) => {
            if (!projectId) return;
            const { undoStack } = historyStore.getState();
            const cleanItems = items.map(cleanItem);
            const payload = {
                edit_history: { body: JSON.stringify(undoStack) },
                items: cleanItems,
            };
            await ProjectService.updateProjectCueSheetItemsAsync(projectId, payload);
        },

        syncCueSheetWithHistory: async (projectId, label, patch) => {
            const { undoStack } = historyStore.getState();
            const cueSheet = get('cueSheet');

            const changedIds = new Set([
                ...Object.keys(patch.changed),
                ...patch.added.map(i => i.id),
            ]);

            const items = cueSheet
                .filter(item => changedIds.has(item.id))
                .map(item => cleanItem(item));

            const payload = {
                edit_history: { body: JSON.stringify(undoStack) },
                items,
            };

            await ProjectService.updateProjectCueSheetItemsAsync(projectId, payload);
        },
    };
};
